import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import LoginPage from './LoginPage';
import ProductOverview from './ProductOverview';
import ProductDetail from './ProductDetail';
import ProductAdvisor from './ProductAdvisor';
import AppShell from './ui/AppShell';
import { supabase } from './supabaseClient';

const AUTH_STORAGE_KEY = 'premtech_authed';

function RequireAuth({ authed, authReady = true, children }) {
  if (!authReady) return null;
  if (!authed) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  const usingSupabase = Boolean(supabase);

  const initialAuthed = useMemo(() => {
    // Fallback only (when Supabase isn't configured)
    return window.localStorage.getItem(AUTH_STORAGE_KEY) === 'true';
  }, []);

  const [authed, setAuthed] = useState(usingSupabase ? false : initialAuthed);
  const [authReady, setAuthReady] = useState(!usingSupabase);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!supabase) return;

    let mounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        const session = data?.session || null;
        setAuthed(Boolean(session));
        setUser(session?.user || null);
        setAuthReady(true);
      })
      .catch(() => {
        if (!mounted) return;
        setAuthed(false);
        setUser(null);
        setAuthReady(true);
      });

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthed(Boolean(session));
      setUser(session?.user || null);
      setAuthReady(true);
    });

    return () => {
      mounted = false;
      data?.subscription?.unsubscribe();
    };
  }, [usingSupabase]);

  const handleLogout = () => {
    if (supabase) {
      supabase.auth.signOut().catch(() => {
        // ignore
      });
      return;
    }

    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuthed(false);
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell authed={authed} onLogout={handleLogout} />}>
          <Route
            path="/"
            element={<Navigate to={authed ? '/products' : '/login'} replace />}
          />

          <Route
            path="/login"
            element={
              authReady && authed ? (
                <Navigate to="/products" replace />
              ) : (
                <LoginPage
                  onLoginSuccess={() => {
                    if (!supabase) {
                      window.localStorage.setItem(AUTH_STORAGE_KEY, 'true');
                      setAuthed(true);
                    }
                  }}
                />
              )
            }
          />

          <Route
            path="/products"
            element={
              <RequireAuth authed={authed} authReady={authReady}>
                <ProductOverview />
              </RequireAuth>
            }
          />
          <Route
            path="/products/:id"
            element={
              <RequireAuth authed={authed} authReady={authReady}>
                <ProductDetail />
              </RequireAuth>
            }
          />

          <Route
            path="/advisor"
            element={
              <RequireAuth authed={authed} authReady={authReady}>
                <ProductAdvisor user={user} />
              </RequireAuth>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
